import { ACCESS_TOKEN_NAME, DATE_FORMAT, ROLE_TYPES, USER_ROLE } from 'api/constants';
import dayjs from 'dayjs';

export const getRole = () => {
  return localStorage.getItem(USER_ROLE);
};

export const getAuthToken = (domain) => {
  const tokenMap = {
    [ROLE_TYPES.admin]: localStorage.getItem(ACCESS_TOKEN_NAME.admin),
    [ROLE_TYPES.slh_manager]: localStorage.getItem(ACCESS_TOKEN_NAME.slh_manager),
    [ROLE_TYPES.client]: localStorage.getItem(ACCESS_TOKEN_NAME.client),
  };

  return tokenMap[domain];
};

export const isInRoute = (path, route) => {
  return path.includes(route);
};

export const buildURL = (baseURL, endpoint, params) => {
  if (!params) return `${baseURL}${endpoint}`;

  const url = new URL(endpoint, baseURL);

  Object.keys(params).forEach((key) => {
    if (params[key] !== undefined && params[key] !== null) {
      if (Array.isArray(params[key])) {
        params[key].forEach((value) => {
          if (value !== undefined && value !== null && value !== '') {
            url.searchParams.append(key, value);
          }
        });
      } else {
        url.searchParams.append(key, params[key]);
      }
    }
  });

  return url.toString();
};

export const getBaseUrl = (url) => {
  const urlPattern = /^(https?:\/\/[^\s/$.?#].[^\s]*)$/;
  if (urlPattern.test(url)) {
    const urlObj = new URL(url);
    return `${urlObj.origin}${urlObj.pathname}`.slice(0, -1);
  }
  return null;
};

export const splitName = (name) => {
  const [firstName, ...lastNameParts] = name.split(' ');
  const lastName = lastNameParts.join(' ');
  return { firstName, lastName };
};

export const formatPriceWithComma = (price) => {
  return Number(price)
    ?.toFixed(2)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const formatDate = (date) => {
  return dayjs(date).format(DATE_FORMAT);
};

export const calculateWeeksBetweenDates = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const differenceInMilliseconds = end - start;

  const millisecondsPerWeek = 7 * 24 * 60 * 60 * 1000;
  const weeks = differenceInMilliseconds / millisecondsPerWeek;

  return Math.floor(weeks);
};

export const getIdFromPath = (path) => {
  const segments = path.split('/');
  const userId = segments.pop();

  return parseInt(userId, 10);
};

export const getDynamicParamsFromPath = (path) => {
  const segments = path.split('/');
  const params = segments.filter((i) => Number.isInteger(Number(i))).slice(1, segments.length - 1);

  return params;
};

export function splitPeriodIntoWeeks(startDateStr, endDateStr) {
  const today = new Date();
  const week = new Array(7).fill(false);
  const weeks = new Array(7).fill(week);
  const start = new Date(startDateStr);
  const end = new Date(endDateStr);
  const endWithCurrentCalc = end > today ? today : end;

  const startDay = start.getUTCDay();
  const startMonday = new Date(start);
  startMonday.setUTCDate(start.getUTCDate() - (startDay === 0 ? 6 : startDay - 1));

  const endDay = endWithCurrentCalc.getUTCDay();
  const endSunday = new Date(endWithCurrentCalc);
  endSunday.setUTCDate(endWithCurrentCalc.getUTCDate() + (6 - endDay));

  let currentWeekStart = new Date(startMonday);
  let weekIndex = 0;

  while (currentWeekStart <= endSunday) {
    const weekArray = new Array(7);
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setUTCDate(weekEnd.getUTCDate() + 6);

    for (let i = 0; i < 7; i++) {
      const day = new Date(currentWeekStart);
      day.setUTCDate(day.getUTCDate() + i);

      weekArray[i] = day >= start && day < endWithCurrentCalc;
    }

    weeks[weekIndex] = weekArray;
    if (weekIndex < 6) weekIndex++;
    currentWeekStart.setUTCDate(currentWeekStart.getUTCDate() + 7);
  }

  return weeks;
}

export const getActiveWeekAndDaysSinceStart = (startDate, endDate) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const today = dayjs();

  let activeWeek, daysSinceStart;

  if (today.isBefore(start)) {
    activeWeek = -1;
    daysSinceStart = 0;
  } else if (today.isAfter(end)) {
    activeWeek = 7;
    daysSinceStart = 42;
  } else {
    daysSinceStart = today.diff(start, 'days');
    activeWeek = Math.floor(daysSinceStart / 7) + 1;
  }

  return { activeWeek, daysSinceStart };
};

export const getClientTaskProgressState = (task, weekIndex, lastAssignedWeek, lastAssignedAt, now = dayjs()) => {
  if (task?.completed) return 'completed';

  const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
  const diffFromLatest = lastAssignedAt
    ? now.valueOf() - dayjs(lastAssignedAt).valueOf()
    : Infinity;

  if (!task?.assigned_at) {
    // Next unassigned week is 'current' while the latest task's 7-day window is still open
    if (weekIndex === lastAssignedWeek + 1 && diffFromLatest < oneWeekMs) return 'current';
    return 'upcoming';
  }

  // A newer task was generated after this one — it is overdue regardless of time
  if (weekIndex < lastAssignedWeek) return 'overdue';

  // weekIndex === lastAssignedWeek: the latest assigned task, not completed
  if (diffFromLatest < oneWeekMs) return 'pending'; // within 7-day submission window
  return 'overdue'; // past deadline
};

export function removeDuplicates(arr) {
  const seen = new Set();
  return arr.filter((item) => {
    const key = `${item.label}-${item.value}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function buildSlhHierarchy(list) {
  const lookup = list.reduce((map, item) => {
    map[item.id] = { ...item, children: [] };
    return map;
  }, {});

  const roots = [];
  list.forEach((item) => {
    const node = lookup[item.id];
    const parentNode = item.parent && lookup[item.parent];

    if (item.type === 'child' && parentNode) {
      parentNode.children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

export function buildSelectOptions(partners) {
  const lookup = new Map();
  const topLevel = [];

  // Initialize lookup with all partners
  partners.forEach((partner) => {
    lookup.set(partner.value, {
      key: partner.value * Math.random(),
      label: partner.label,
      value: partner.value,
      type: partner.type,
      children: [],
    });
  });

  // Build hierarchy
  partners.forEach((partner) => {
    const node = lookup.get(partner.value);
    if (partner.type === 'child' && partner.parent) {
      const parentNode = lookup.get(partner.value);
      if (parentNode) {
        parentNode.children.push(node);
      } else {
        topLevel.push(node); // orphan child
      }
    } else {
      topLevel.push(node); // parent or non-relational child
    }
  });
  return topLevel;
}

export const blockInvalidChar = (e) => {
  if (['+', '-'].includes(e.key)) {
    e.preventDefault();
  }

  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
    e.preventDefault();
  }
};

/**
 * Sanitize input before sending to server.
 * - For strings: replace newlines with spaces, collapse multiple whitespace to single space, trim ends.
 * - For arrays/objects: recurse and sanitize all string values.
 */
export function sanitizeForServer(input) {
  if (input === null || input === undefined) return input;

  if (typeof input === 'string') {
    return input
      .replace(/[\r\n]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  if (Array.isArray(input)) {
    return input.map((item) => sanitizeForServer(item));
  }

  if (typeof input === 'object') {
    const out = {};
    Object.keys(input).forEach((key) => {
      out[key] = sanitizeForServer(input[key]);
    });
    return out;
  }

  return input;
}

export const allowOnlyDigits = (e) => {
  if (
    e.key === 'Backspace' ||
    e.key === 'Delete' ||
    e.key === 'Tab' ||
    e.key === 'ArrowLeft' ||
    e.key === 'ArrowRight' ||
    e.key === 'Home' ||
    e.key === 'End'
  ) {
    return;
  }

  if (e.ctrlKey || e.metaKey) {
    return;
  }

  if (!/^\d$/.test(e.key)) {
    e.preventDefault();
  }
};

export const openUrlWithRole = (url, role) => {
  try {
    const isAbsolute = /^(?:[a-z]+:)?\/\//i.test(url);
    const base = isAbsolute ? url : new URL(url, window.location.origin).toString();
    const u = new URL(base);
    if (role) u.searchParams.set('role', role);
    window.open(u.toString(), '_blank');
  } catch (err) {
    const sep = url.includes('?') ? '&' : '?';
    window.open(`${url}${sep}role=${role}`, '_blank');
  }
};

/**
 * Reorder sober living homes so that:
 * - All parents come first, and
 * - Immediately after each parent are its children (matched via child's `parent` field),
 * - Then all `no_relationship` items at the end.
 */

export const orderHomes = (list) => {
  const parents = [];
  const nonRelationships = [];
  const childrenByParentId = new Map();

  for (const item of list) {
    if (item.type === 'parent') {
      parents.push(item);
    } else if (item.type === 'no_relationship') {
      nonRelationships.push(item);
    } else if (item.type === 'child' && item.parent != null) {
      if (!childrenByParentId.has(item.parent)) {
        childrenByParentId.set(item.parent, []);
      }
      childrenByParentId.get(item.parent).push(item);
    }
  }

  parents.sort((a, b) => a.id - b.id);

  // eslint-disable-next-line no-unused-vars
  for (const [parentId, children] of childrenByParentId.entries()) {
    children.sort((a, b) => {
      const nameA = (a.name || '').toString().toLowerCase();
      const nameB = (b.name || '').toString().toLowerCase();
      if (nameA === nameB) return (a.id || 0) - (b.id || 0);
      return nameA < nameB ? -1 : 1;
    });
  }

  const result = [];

  for (const parent of parents) {
    result.push(parent);
    const children = childrenByParentId.get(parent.id);
    if (children && children.length) {
      result.push(...children);
    }
  }

  const sortedParents = parents.sort((a, b) => {
    const nameA = (a.name || '').toString().toLowerCase();
    const nameB = (b.name || '').toString().toLowerCase();
    if (nameA === nameB) return (a.id || 0) - (b.id || 0);
    return nameA < nameB ? -1 : 1;
  });

  const final = [];
  for (const parent of sortedParents) {
    final.push(parent);
    const children = childrenByParentId.get(parent.id);
    if (children && children.length) {
      final.push(...children);
    }
  }

  nonRelationships.sort((a, b) => {
    const nameA = (a.name || '').toString().toLowerCase();
    const nameB = (b.name || '').toString().toLowerCase();
    if (nameA === nameB) return (a.id || 0) - (b.id || 0);
    return nameA < nameB ? -1 : 1;
  });

  final.push(...nonRelationships);
  return final;
};
